import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { adminApi, extractRefreshToken, extractToken, extractUser } from './../lib/admin-api/index.js';
import { isTenantRestrictedModeActiveError } from '../lib/admin-api/http-client.js';
import { getUserPermissions, hasAnyPermission as checkAnyPermission, hasPermission as checkPermission } from '../modules/system-navigation/model/permission-visibility.js';

const TOKEN_KEY = 'admin_auth_token';
const REFRESH_TOKEN_KEY = 'admin_auth_refresh_token';
const USER_KEY = 'admin_auth_user';
export const RESTRICTED_MODE_NOTICE_KEY = 'admin_auth_restricted_mode_notice';
const SESSION_STATUS_POLL_INTERVAL_MS = 60000;

const AuthContext = createContext(null);

function readInitialToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function readInitialUser() {
  try {
    const rawUser = localStorage.getItem(USER_KEY);
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
}

function readInitialRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function rememberRestrictedModeNotice(error) {
  if (!isTenantRestrictedModeActiveError(error)) {
    return false;
  }

  try {
    sessionStorage.setItem(RESTRICTED_MODE_NOTICE_KEY, '1');
  } catch {
    // ignore storage errors
  }

  return true;
}

function clearRestrictedModeNotice() {
  try {
    sessionStorage.removeItem(RESTRICTED_MODE_NOTICE_KEY);
  } catch {
    // ignore storage errors
  }
}

function shouldRedirectRestrictedModeFailure(pathname) {
  return pathname !== '/login' && !pathname.startsWith('/support-access');
}

export function AuthProvider({ children }) {
  const initialToken = readInitialToken();
  const initialRefreshToken = readInitialRefreshToken();
  const [token, setToken] = useState(initialToken);
  const [refreshToken, setRefreshToken] = useState(initialRefreshToken);
  const [user, setUser] = useState(readInitialUser);
  const [isLoading, setIsLoading] = useState(Boolean(initialToken));
  const authFlowVersionRef = useRef(0);
  const focusRefreshInFlightRef = useRef(false);
  const tokenRef = useRef(initialToken);
  const refreshTokenRef = useRef(initialRefreshToken);
  const userRef = useRef(readInitialUser());

  const persistState = useCallback((nextToken, nextRefreshToken, nextUser) => {
    if (nextToken) {
      localStorage.setItem(TOKEN_KEY, nextToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }

    if (nextRefreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, nextRefreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    if (nextUser) {
      localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(USER_KEY);
    }
  }, []);

  const beginAuthFlow = useCallback(() => {
    authFlowVersionRef.current += 1;

    return authFlowVersionRef.current;
  }, []);

  const isAuthFlowCurrent = useCallback((expectedVersion) => (
    expectedVersion === undefined || authFlowVersionRef.current === expectedVersion
  ), []);

  const writeSession = useCallback((nextToken, nextRefreshToken, nextUser, expectedVersion) => {
    if (!isAuthFlowCurrent(expectedVersion)) {
      return false;
    }

    setToken(nextToken);
    setRefreshToken(nextRefreshToken);
    setUser(nextUser);
    tokenRef.current = nextToken;
    refreshTokenRef.current = nextRefreshToken;
    userRef.current = nextUser;
    persistState(nextToken, nextRefreshToken, nextUser);

    return true;
  }, [isAuthFlowCurrent, persistState]);

  const clearSession = useCallback(() => {
    beginAuthFlow();
    adminApi.clearDictionaryCache();
    writeSession(null, null, null);
  }, [beginAuthFlow, writeSession]);

  const refreshCurrentUser = useCallback(async (
    accessToken = token,
    activeRefreshToken = refreshToken,
    expectedVersion,
    { retryOnForbidden = true } = {},
  ) => {
    if (!accessToken) {
      return null;
    }

    if (!isAuthFlowCurrent(expectedVersion)) {
      return null;
    }

    const response = retryOnForbidden
      ? await adminApi.getCurrentUser(accessToken)
      : await adminApi.getCurrentUser(accessToken, { retryOnForbidden: false });
    const nextUser = extractUser(response) || userRef.current;
    const resolvedToken = tokenRef.current || accessToken;
    const resolvedRefreshToken = refreshTokenRef.current || activeRefreshToken;

    if (!writeSession(resolvedToken, resolvedRefreshToken, nextUser, expectedVersion)) {
      return null;
    }

    return nextUser;
  }, [token, refreshToken, isAuthFlowCurrent, writeSession]);

  useEffect(() => {
    adminApi.configureAuth({
      getAccessToken: () => tokenRef.current,
      getRefreshToken: () => refreshTokenRef.current,
      getAuthFlowVersion: () => authFlowVersionRef.current,
      onAuthTokens: ({ token: nextToken, refreshToken: nextRefreshToken, authFlowVersion }) => {
        if (!isAuthFlowCurrent(authFlowVersion)) {
          return;
        }

        const resolvedRefreshToken = nextRefreshToken || refreshTokenRef.current;
        writeSession(nextToken, resolvedRefreshToken, userRef.current, authFlowVersion);
      },
      onAuthFailure: (_error, metadata = {}) => {
        if (!isAuthFlowCurrent(metadata.authFlowVersion)) {
          return;
        }

        rememberRestrictedModeNotice(_error);
        clearSession();
        if (shouldRedirectRestrictedModeFailure(window.location.pathname)) {
          window.location.replace('/login');
        }
      },
    });
  }, [token, refreshToken, user, beginAuthFlow, isAuthFlowCurrent, writeSession, clearSession]);

  const authorizeResponse = useCallback(async (response, nextToken = extractToken(response), nextRefreshToken = extractRefreshToken(response)) => {
    if (!nextToken) {
      throw new Error('API did not return an authorization token');
    }

    clearRestrictedModeNotice();
    const authFlowVersion = beginAuthFlow();
    const responseUser = extractUser(response);

    if (responseUser) {
      writeSession(nextToken, nextRefreshToken, responseUser, authFlowVersion);
    } else {
      writeSession(nextToken, nextRefreshToken, null, authFlowVersion);
      await refreshCurrentUser(nextToken, nextRefreshToken, authFlowVersion);
    }

    return nextToken;
  }, [beginAuthFlow, writeSession, refreshCurrentUser]);

  const login = useCallback(async ({ identifier, password }) => {
    const response = await adminApi.login(identifier, password);
    if (response?.requires2fa && response?.challengeId) {
      return {
        status: 'requires2fa',
        challengeId: response.challengeId,
        method: response.method || 'totp',
        expiresAt: response.expiresAt || null,
      };
    }

    const nextToken = extractToken(response);
    const nextRefreshToken = extractRefreshToken(response);

    await authorizeResponse(response, nextToken, nextRefreshToken);

    return {
      status: 'authenticated',
      token: nextToken,
    };
  }, [authorizeResponse]);

  const completeTwoFactor = useCallback(async ({ challengeId, code }) => {
    const response = await adminApi.verifyTwoFactor(challengeId, code);
    const nextToken = await authorizeResponse(response);

    return {
      status: 'authenticated',
      token: nextToken,
    };
  }, [authorizeResponse]);

  const consumeSupportAccess = useCallback(async (inviteToken) => {
    let response;
    try {
      response = await adminApi.consumeSupportAccessInvite(inviteToken);
    } catch (error) {
      if (rememberRestrictedModeNotice(error)) {
        clearSession();
      }

      throw error;
    }

    const nextToken = await authorizeResponse(response);

    return {
      status: 'authenticated',
      token: nextToken,
    };
  }, [authorizeResponse, clearSession]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    let isCancelled = false;
    const bootstrapAuthFlowVersion = authFlowVersionRef.current;

    const bootstrap = async () => {
      if (!token) {
        if (isAuthFlowCurrent(bootstrapAuthFlowVersion)) {
          setIsLoading(false);
        }
        return;
      }

      try {
        await refreshCurrentUser(token, refreshToken, bootstrapAuthFlowVersion);
	      } catch (error) {
	        if (!isCancelled && isAuthFlowCurrent(bootstrapAuthFlowVersion)) {
	          rememberRestrictedModeNotice(error);
	          clearSession();
	          if (error?.status === 401 || error?.status === 403) {
	            window.location.replace('/login');
	          }
	        }
	      } finally {
        if (!isCancelled && isAuthFlowCurrent(bootstrapAuthFlowVersion)) {
          setIsLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      isCancelled = true;
    };
  }, [token, refreshToken, refreshCurrentUser, clearSession, isAuthFlowCurrent]);

  useEffect(() => {
    if (!token) {
      return;
    }

    adminApi.getCountryDictionary(token).catch(() => {});
    adminApi.getCurrenciesDictionary(token).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    let isCancelled = false;

    const refreshVisibleSession = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') {
        return;
      }

      if (focusRefreshInFlightRef.current) {
        return;
      }

      const focusAuthFlowVersion = authFlowVersionRef.current;
      focusRefreshInFlightRef.current = true;

      refreshCurrentUser(tokenRef.current, refreshTokenRef.current, focusAuthFlowVersion)
        .catch((error) => {
          if (isCancelled || !isAuthFlowCurrent(focusAuthFlowVersion)) {
            return;
          }

	          if (rememberRestrictedModeNotice(error) || error?.status === 401 || error?.status === 403) {
	            clearSession();
	            if (shouldRedirectRestrictedModeFailure(window.location.pathname)) {
	              window.location.replace('/login');
	            }
          }
        })
        .finally(() => {
          focusRefreshInFlightRef.current = false;
        });
    };

    window.addEventListener('focus', refreshVisibleSession);
    document.addEventListener('visibilitychange', refreshVisibleSession);
    const sessionStatusPoll = window.setInterval(refreshVisibleSession, SESSION_STATUS_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.removeEventListener('focus', refreshVisibleSession);
      document.removeEventListener('visibilitychange', refreshVisibleSession);
      window.clearInterval(sessionStatusPoll);
    };
  }, [token, refreshToken, refreshCurrentUser, clearSession, isAuthFlowCurrent]);

  const permissions = useMemo(() => getUserPermissions(user), [user]);
  const hasPermission = useCallback((permission) => checkPermission(permissions, permission), [permissions]);
  const hasAnyPermission = useCallback((nextPermissions) => checkAnyPermission(permissions, nextPermissions), [permissions]);

  const value = useMemo(
    () => ({
      token,
      refreshToken,
      user,
      permissions,
      isLoading,
      isAuthenticated: Boolean(token),
      hasPermission,
      hasAnyPermission,
      login,
      completeTwoFactor,
      consumeSupportAccess,
      logout,
      refreshCurrentUser,
    }),
    [token, refreshToken, user, permissions, isLoading, hasPermission, hasAnyPermission, login, completeTwoFactor, consumeSupportAccess, logout, refreshCurrentUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
